import type { BookingSessionResponse } from '@massilia/queries'

import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

import { TRIP_TYPE_LABELS } from '@/utils/trip-labels'

interface TripHeaderProps {
  session: BookingSessionResponse
}

export function TripHeader({ session }: TripHeaderProps) {
  const flightDetails = session.flightDetails
  const { arrival, departure } = getAirportDisplayNames(session)
  const tripLabel = TRIP_TYPE_LABELS[flightDetails.tripType] ?? flightDetails.tripType
  const passengerCount = getPassengerCount(session)
  const departureFormatted = formatTripDate(flightDetails.departureDate)
  const lastDate = flightDetails.returnDate ?? getLastJourneyDate(session)
  const returnFormatted =
    flightDetails.tripType !== 'one-way' && lastDate ? formatTripDate(lastDate) : undefined

  return (
    <div className="flex flex-col gap-2">
      <div className="text-gray-light-600 flex items-center gap-2 text-xs font-medium">
        <span>{tripLabel}</span>
        <span>&bull;</span>
        <span>
          {passengerCount} passager{passengerCount > 1 ? 's' : ''}
        </span>
        <span>&bull;</span>
        <span>
          {departureFormatted}
          {returnFormatted && ` - ${returnFormatted}`}
        </span>
      </div>
      <h1 className="text-gray-light-900 text-2xl font-semibold">
        Voyage de {departure} vers {arrival}
      </h1>
    </div>
  )
}

function formatTripDate(isoDate: string): string {
  try {
    return format(new Date(isoDate), 'EEE d MMM', { locale: fr })
  } catch {
    return isoDate
  }
}

function getAirportDisplayNames(session: BookingSessionResponse): {
  arrival: string
  departure: string
} {
  const journeys = session.priceData?.journeys
  if (journeys && journeys.length > 0) {
    const firstJourney = journeys[0]
    const segments = firstJourney.segments

    if (segments.length > 0) {
      const departure = segments[0].departure.airport
      const arrival = segments[segments.length - 1].arrival.airport

      if (departure && arrival) {
        return { arrival, departure }
      }
    }
  }

  // Fallback: use flightDetails search labels
  return {
    arrival: session.flightDetails.destination,
    departure: session.flightDetails.origin,
  }
}

function getLastJourneyDate(session: BookingSessionResponse): null | string {
  const journeys = session.priceData?.journeys
  if (!journeys || journeys.length === 0) return null
  const lastJourney = journeys[journeys.length - 1]
  const segments = lastJourney.segments
  if (segments.length === 0) return null
  return segments[segments.length - 1].arrival.date
}

function getPassengerCount(session: BookingSessionResponse): number {
  if (session.passengers && session.passengers.length > 0) {
    return session.passengers.length
  }
  const counts = session.flightDetails.passengersCount
  return Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0)
}
